import { useState, useEffect, useRef, useCallback } from "react";
import { X } from 'lucide-react';
import authService from '../services/AuthService';
import { idbLoadSet, idbSaveSet, idbDeleteKey, migrateKeysFromLocalStorage } from '../services/idbStorage';

const AMAZON_CATEGORY_WHITELIST = new Set([
    "Alexa Skills", "Amazon Autos", "Amazon Devices & Accessories", "Amazon Luxury",
    "Appliances", "Apps & Games", "Arts, Crafts & Sewing", "Audible Books & Originals",
    "Automotive", "Baby Products", "Beauty & Personal Care", "Books", "CDs & Vinyl",
    "Cell Phones & Accessories", "Clothing, Shoes & Jewelry", "Collectibles & Fine Art",
    "Digital Music", "Electronics", "Everything Else", "Gift Cards",
    "Grocery & Gourmet Food", "Handmade Products", "Health & Household",
    "Home & Kitchen", "Industrial & Scientific", "Kindle Store",
    "Magazine Subscriptions", "Movies & TV", "Musical Instruments", "Office Products",
    "Patio, Lawn & Garden", "Pet Supplies", "Software", "Sports & Outdoors",
    "Tools & Home Improvement", "Toys & Games", "Video Games", "Video Shorts",
]);

// ─── Storage helpers ──────────────────────────────────────────────────────────
const SK_CHECKED_ASINS_V2 = "uc2_checked_asins";
const SK_SEEN_BRANDS_V2 = "uc2_seen_brands";       // brands confirmed NEW this/previous runs (for display)
const SK_ASKED_BRANDS_V2 = "uc2_asked_brands";     // ALL brands ever sent to record-brands (new or not)

async function saveHistoricalLead(leadObj) {
    try {
        await authService.authenticatedFetch(`${API_BASE}/historical-leads/save`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(leadObj),
        });
    } catch { /* best-effort, ignore */ }
}

// ─── YouTube video IDs ────────────────────────────────────────────────────────
const YT_VIDEOS = [
    "rtlDOpvow3s",
    "sFWdAhKzLuw",
    "6IAeczSBXyI",
    "Li7iGXo3Sfk",
    "Ll5H_Jh1TFM",
    "OZnUgisQLvU",
    "JWLRxRK_Acw",
];

const API_BASE = "https://server.multisellerpro.com/api";
const MAX_BRANDS_PER_RUN = 100;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// ─── Crawl phases ─────────────────────────────────────────────────────────────
const PHASE = {
    IDLE: "idle",
    FETCH_BATCH: "fetch_batch",
    CHECK_GATING: "check_gating",
};

// ─── Animated dots ────────────────────────────────────────────────────────────
function Dots() {
    const [d, setD] = useState(1);
    useEffect(() => {
        const t = setInterval(() => setD(p => (p % 3) + 1), 500);
        return () => clearInterval(t);
    }, []);
    return <span>{".".repeat(d)}</span>;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UngateCrawlerV2({ onClose }) {

    // ── UI state ──────────────────────────────────────────────────────────────
    const [started, setStarted] = useState(false);
    const [running, setRunning] = useState(false);
    const [phase, setPhase] = useState(PHASE.IDLE);
    const [stats, setStats] = useState({ checked: 0, newBrands: 0 });
    const [newBrandsThisRun, setNewBrandsThisRun] = useState([]);
    const [ytIdx, setYtIdx] = useState(0);
    const [foundProducts, setFoundProducts] = useState([]);
    const [showFoundProducts, setShowFoundProducts] = useState(false);
    const [showVideos, setShowVideos] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]); // [] = all
    const [showCategoryPicker, setShowCategoryPicker] = useState(false);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [queueSize, setQueueSize] = useState(0);

    // ── Brand list filter (mutually exclusive with category filter) ──────────
    const [showBrandListPicker, setShowBrandListPicker] = useState(false);
    const [brandListText, setBrandListText] = useState("");
    const [brandListMode, setBrandListMode] = useState(false); // true once "Apply" is pressed
    const [brandListFinished, setBrandListFinished] = useState(false); // true once a brand-list run completes naturally
    const [brandListGroupInfo, setBrandListGroupInfo] = useState({ current: 0, total: 0 }); // which group of 100 we're on

    // ── Persistence refs ──────────────────────────────────────────────────────
    const checkedAsins = useRef(new Set());
    const seenBrands = useRef(new Set());
    const askedBrands = useRef(new Set()); // never re-call record-brands for these
    const checkQueue = useRef([]); // [{asin, brand, category}] - ephemeral, not persisted
    const brandListGroupIndex = useRef(0); // which group of MAX_BRANDS_PER_RUN brands we're currently on

    // ── Control refs ──────────────────────────────────────────────────────────
    const stopFlag = useRef(false);
    const statsRef = useRef({ checked: 0, newBrands: 0 });

    // Restore persisted state on mount
    useEffect(() => {
        (async () => {
            await migrateKeysFromLocalStorage([SK_CHECKED_ASINS_V2, SK_SEEN_BRANDS_V2, SK_ASKED_BRANDS_V2]);
            checkedAsins.current = await idbLoadSet(SK_CHECKED_ASINS_V2);
            seenBrands.current = await idbLoadSet(SK_SEEN_BRANDS_V2);
            askedBrands.current = await idbLoadSet(SK_ASKED_BRANDS_V2);

            const cached = [...seenBrands.current];
            if (cached.length) setNewBrandsThisRun(cached);
            statsRef.current.checked = checkedAsins.current.size;
            setStats({ checked: checkedAsins.current.size, newBrands: cached.length });
        })();

        authService.authenticatedFetch(`${API_BASE}/user/ungated-brands/v2/categories`)
            .then(r => r.json())
            .then(d => setAvailableCategories((d.categories || []).filter(c => AMAZON_CATEGORY_WHITELIST.has(c))))
            .catch(() => { });
    }, []);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const pushStats = useCallback((patch) => {
        statsRef.current = { ...statsRef.current, ...patch };
        setStats({ ...statsRef.current });
    }, []);

    const refreshQueueSize = useCallback(() => {
        setQueueSize(checkQueue.current.length);
    }, []);

    const parseBrandList = (raw) =>
        [...new Set(raw.split(/[\n,]/).map(s => s.trim()).filter(Boolean))];

    const chunkBrandList = (raw, size = MAX_BRANDS_PER_RUN) => {
        const unique = parseBrandList(raw);
        const groups = [];
        for (let i = 0; i < unique.length; i += size) groups.push(unique.slice(i, i + size));
        return groups;
    };

    // ── API: fetch ASINs for a specific list of brand names (one-shot) ────────
    const fetchBrandListBatch = useCallback(async (brands) => {
        const res = await authService.authenticatedFetch(
            `${API_BASE}/user/ungated-brands/v2/brand-list-batch`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ brands }),
            }
        );
        const data = await res.json();
        return data.asins || [];
    }, []);

    // ── API: fetch a fresh random batch of ASINs (brand + category attached) ──
    const fetchNextBatch = useCallback(async () => {
        const res = await authService.authenticatedFetch(
            `${API_BASE}/user/ungated-brands/v2/next-batch`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    categories: selectedCategories,
                    brands_per_batch: 100,
                    per_brand: 10,
                }),
            }
        );
        const data = await res.json();
        return data.asins || [];
    }, [selectedCategories]);

    // ── API: check gating for a batch ────────────────────────────────────────
    const checkGatingBatch = useCallback(async (asins) => {
        const res = await authService.authenticatedFetch(
            `${API_BASE}/a2a/check-gated-bulk`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ asins, skip_cache: true }),
            }
        );
        if (!res.ok) return {};
        const data = await res.json();
        return data.results || {};
    }, []);

    // ── API: record newly-confirmed ungated brands ───────────────────────────
    const recordBrands = useCallback(async (brands) => {
        const res = await authService.authenticatedFetch(
            `${API_BASE}/user/ungated-brands/v2/record-brands`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ brands }),
            }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.new_brands || [];
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // Main crawl loop
    //
    //   1. Refill queue: pull a fresh random batch of ASINs (10 brands x up
    //      to 100 asins each) - brand + category already known, no lookup.
    //   2. Drain queue in chunks of 10 -> check gating.
    //   3. Ungated ASIN -> enrich (title/image/BSR), record its brand.
    //   4. Queue empty -> pull another fresh random batch.
    // ─────────────────────────────────────────────────────────────────────────
    const crawl = useCallback(async () => {
        while (!stopFlag.current) {

            if (checkQueue.current.length === 0) {
                if (brandListMode) {
                    const groups = chunkBrandList(brandListText);
                    if (groups.length === 0 || brandListGroupIndex.current >= groups.length) {
                        // All groups processed — stop instead of looping forever
                        setBrandListFinished(true);
                        stopFlag.current = true;
                        break;
                    }

                    const thisGroupIdx = brandListGroupIndex.current;
                    setBrandListGroupInfo({ current: thisGroupIdx + 1, total: groups.length });
                    setPhase(PHASE.FETCH_BATCH);
                    let batch = [];
                    try {
                        batch = await fetchBrandListBatch(groups[thisGroupIdx]);
                    } catch { await sleep(3000); continue; }

                    brandListGroupIndex.current = thisGroupIdx + 1;

                    batch = batch.filter(a => !checkedAsins.current.has(a.asin));
                    checkQueue.current = batch;
                    refreshQueueSize();

                    if (batch.length === 0) {
                        // Nothing new in this group — loop picks up the next group automatically
                        continue;
                    }
                } else {
                    setPhase(PHASE.FETCH_BATCH);
                    let batch = [];
                    try {
                        batch = await fetchNextBatch();
                    } catch { await sleep(3000); continue; }

                    batch = batch.filter(a => !checkedAsins.current.has(a.asin));
                    checkQueue.current = batch;
                    refreshQueueSize();

                    if (batch.length === 0) {
                        await sleep(1500);
                        continue;
                    }
                }
            }
            if (stopFlag.current) break;

            setPhase(PHASE.CHECK_GATING);
            const chunk = checkQueue.current.splice(0, 10);
            const meta = new Map(chunk.map(a => [a.asin, a]));
            refreshQueueSize();

            let results = {};
            try {
                results = await checkGatingBatch(chunk.map(a => a.asin));
            } catch { await sleep(4500); continue; }

            chunk.forEach(a => checkedAsins.current.add(a.asin));
            await idbSaveSet(SK_CHECKED_ASINS_V2, checkedAsins.current);
            pushStats({ checked: statsRef.current.checked + chunk.length });

            if (Object.values(results).some(s => s === "needs_login")) {
                stopFlag.current = true;
                break;
            }

            const ungatedAsins = Object.keys(results).filter(a => results[a] === "ungated" && meta.has(a));

            if (ungatedAsins.length) {
                // ── Step 1: figure out which brands are in THIS batch using the
                // brand/category we already know from the batch metadata (no
                // extra call needed) — and record them BEFORE anything else
                // touches user_ungated_brands. This is the ONLY thing that's
                // allowed to run before record-brands, so its "already exists"
                // check reflects the true prior state.
                const uniqueBrands = [];
                const seenKeys = new Set();
                ungatedAsins.forEach(asin => {
                    const known = meta.get(asin);
                    const brand = (known?.brand || '').trim();
                    if (!brand) return;
                    const brandKey = brand.toLowerCase();
                    if (seenKeys.has(brandKey)) return;
                    if (askedBrands.current.has(brandKey)) return; // already asked, skip entirely
                    seenKeys.add(brandKey);
                    uniqueBrands.push({ brand, category: known.category, brandKey });
                });

                if (uniqueBrands.length) {
                    // Mark these as asked BEFORE the call, so even if the response is slow/fails,
                    // we never fire a duplicate request for the same brand from a later chunk.
                    uniqueBrands.forEach(b => askedBrands.current.add(b.brandKey));
                    await idbSaveSet(SK_ASKED_BRANDS_V2, askedBrands.current);

                    try {
                        const newBrands = await recordBrands(
                            uniqueBrands.map(b => ({ brand: b.brand, category: b.category }))
                        );
                        const genuinelyNew = newBrands.filter(
                            b => !seenBrands.current.has(b.toLowerCase())
                        );
                        if (genuinelyNew.length) {
                            genuinelyNew.forEach(b => seenBrands.current.add(b.toLowerCase()));
                            await idbSaveSet(SK_SEEN_BRANDS_V2, seenBrands.current);
                            setNewBrandsThisRun(prev => [...prev, ...genuinelyNew]);
                        }
                    } catch { /* ignore — brand stays marked "asked" so we don't hammer the endpoint on repeated failures either */ }
                }

                // ── Step 2: enrich each ungated ASIN for title/image/BSR/sales
                // data, purely to build historical leads. It's fine that this
                // call also upserts the brand server-side — the "new brand"
                // decision above already ran against the correct prior state.
                await Promise.all(
                    ungatedAsins.map((asin, idx) =>
                        sleep(idx * 200)
                            .then(() => authService.authenticatedFetch(
                                `${API_BASE}/amazon/cache-ungated-brand/${asin}`,
                                { method: "POST" }
                            ))
                            .then(r => r.json())
                            .then(d => {
                                const known = meta.get(asin);
                                const brand = (d.brand || known.brand || '').trim();
                                const category = d.category || known.category || '';

                                const bsrDrops = d.sales_rank_drops_30 || 0;
                                const verifSales = d.keepa_monthly_sold > 0 ? d.keepa_monthly_sold : 0;
                                if (bsrDrops >= 10 || verifSales > 0) {
                                    const leadObj = {
                                        asin,
                                        brand,
                                        category,
                                        title: d.title || asin,
                                        image: d.image || '',
                                        sales_rank: d.sales_rank || -1,
                                        sales_rank_drops_30: bsrDrops,
                                        keepa_monthly_sold: d.keepa_monthly_sold || -1,
                                        az_price: d.az_price || 0,
                                        bsr_avg30: d.bsr_avg30 || 0,
                                        opened: false,
                                        foundAt: Date.now(),
                                    };
                                    setFoundProducts(prev => {
                                        if (prev.some(p => p.asin === asin)) return prev;
                                        return [leadObj, ...prev];
                                    });
                                    saveHistoricalLead(leadObj);
                                }
                            })
                            .catch(() => { /* enrichment is best-effort only now */ })
                    )
                );
            }

            await sleep(1500);
        }

        setPhase(PHASE.IDLE);
        setRunning(false);
    }, [fetchNextBatch, fetchBrandListBatch, brandListMode, brandListText, checkGatingBatch, recordBrands, pushStats, refreshQueueSize]);

    // ── Start ─────────────────────────────────────────────────────────────────
    const start = useCallback(async () => {
        setLoading(true);
        stopFlag.current = false;
        // A "resume" only preserves brand-list group progress if we paused
        // mid-run. Once a brand-list run has fully finished, pressing Start
        // again ("Scan Brand List Again") should start over from group 1.
        const isResume = started && !brandListFinished;
        if (!isResume) {
            brandListGroupIndex.current = 0;
            setBrandListFinished(false);
            setBrandListGroupInfo({ current: 0, total: brandListMode ? chunkBrandList(brandListText).length : 0 });
        }
        setStarted(true);
        setRunning(true);
        if (!isResume) {
            setNewBrandsThisRun([]);
            setFoundProducts([]);
            seenBrands.current.clear();
            await idbSaveSet(SK_SEEN_BRANDS_V2, seenBrands.current);
            statsRef.current.newBrands = 0;
            setStats(s => ({ ...s, newBrands: 0 }));
        }
        setLoading(false);
        setPhase(PHASE.FETCH_BATCH);
        crawl();
    }, [crawl, started, brandListMode, brandListText, brandListFinished]);

    // ── Stop ──────────────────────────────────────────────────────────────────
    const stop = useCallback(() => {
        stopFlag.current = true;
        setRunning(false);
        setPhase(PHASE.IDLE);
    }, []);

    // ── Reset ─────────────────────────────────────────────────────────────────
    const reset = useCallback(async () => {
        stopFlag.current = true;
        checkedAsins.current.clear();
        seenBrands.current.clear();
        askedBrands.current.clear();
        checkQueue.current = [];
        brandListGroupIndex.current = 0;
        setBrandListGroupInfo({ current: 0, total: 0 });
        setBrandListText("");
        setBrandListMode(false);
        setBrandListFinished(false);
        await Promise.all(
            [SK_CHECKED_ASINS_V2, SK_SEEN_BRANDS_V2, SK_ASKED_BRANDS_V2].map(k => idbDeleteKey(k))
        );
        statsRef.current = { checked: 0, newBrands: 0 };
        setStats({ checked: 0, newBrands: 0 });
        setQueueSize(0);
        setNewBrandsThisRun([]);
        setFoundProducts([]);
        setShowFoundProducts(false);
        setStarted(false);
        setRunning(false);
        setPhase(PHASE.IDLE);
    }, []);

    const exportFoundProducts = useCallback(() => {
        if (!foundProducts.length) return;
        const headers = ["ASIN", "Brand", "Title", "Category", "Price", "BSR", "BSR Drops/mo", "Monthly Sold", "BSR Avg30"];
        const rows = foundProducts.map(p => [
            p.asin,
            `"${(p.brand || '').replace(/"/g, '""')}"`,
            `"${(p.title || '').replace(/"/g, '""')}"`,
            `"${(p.category || '').replace(/"/g, '""')}"`,
            p.az_price > 0 ? p.az_price.toFixed(2) : '',
            p.sales_rank > 0 ? p.sales_rank : '',
            p.sales_rank_drops_30 > 0 ? p.sales_rank_drops_30 : '',
            p.keepa_monthly_sold > 0 ? p.keepa_monthly_sold : '',
            p.bsr_avg30 > 0 ? p.bsr_avg30 : '',
        ]);
        const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "ungated_products.csv"; a.click();
        URL.revokeObjectURL(url);
    }, [foundProducts]);

    const isRunning = running && phase !== PHASE.IDLE;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: "fixed", inset: 0, zIndex: 60,
                background: "rgba(0,0,0,0.52)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 16,
            }}
        >
            <style>{`
                @keyframes uc-border-spin {
                    0%   { background-position: 0% 0%; }
                    100% { background-position: 300% 0%; }
                }
                @keyframes uc-pulse-num {
                    from { opacity: 0.6; transform: scale(0.97); }
                    to   { opacity: 1;   transform: scale(1); }
                }
                .uc-badge {
                    display: inline-block;
                    background: #ecfdf5;
                    color: #065f46;
                    border: 1px solid #6ee7b7;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 2px 9px;
                    border-radius: 999px;
                }
            `}</style>

            <div style={{
                background: "white", borderRadius: 20, width: "100%", maxWidth: 520,
                boxShadow: "0 32px 80px rgba(0,0,0,0.22)", marginTop: "3%",
                display: "flex", flexDirection: "column", overflow: "hidden",
            }}>

                {/* ── Header ── */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "18px 20px 16px", borderBottom: "1px solid #f3f4f6"
                }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: "linear-gradient(135deg,#059669,#34d399)",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                        <svg fill="#ffffff" version="1.1" id="Capa_1" viewBox="0 0 71.682 71.682" stroke="#ffffff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <g> <path d="M48.161,33.825l0.672,0.447c-1.121,0.673-2.912,1.12-5.377,1.567V34.72c2.017-2.017,3.137-4.704,3.137-7.616h1.347v-5.376 h-4.033v5.376h1.344c0,2.465-0.896,4.479-2.24,6.271l-3.584-4.032h-7.617l-3.359,3.81c-1.344-1.568-2.24-3.81-2.24-6.272h1.344 v-5.376h-4.031v5.601h1.344c0,2.912,1.12,5.601,3.136,7.616v1.119c-2.24-0.447-4.032-0.896-5.376-1.567l0.672-0.447l-2.688-4.704 l-3.584,2.016l2.688,4.704l1.344-0.896c1.568,1.119,4.032,1.792,6.944,2.24v2.463c-1.344,1.346-2.464,3.139-2.912,5.151h-1.567 v5.375h4.031v-5.149h-0.896c0.448-1.344,1.12-2.688,2.016-3.81l3.136,3.81h7.617l3.137-3.81c0.896,1.119,1.566,2.466,2.017,3.81 H43.68v5.377h4.031v-5.377h-1.568c-0.447-2.019-1.565-3.81-2.91-5.15v-2.465c2.91-0.447,5.375-1.346,6.943-2.241l1.344,0.896 l2.688-4.702l-3.584-2.018L48.161,33.825z"></path>  </g> </g> </g></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>
                            Ungate Crawler
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>
                            Discovers new ungated brands AUTOMATICALLY
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: 28, height: 28, borderRadius: "50%", border: "none",
                            background: "#f3f4f6", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#6b7280"
                        }}
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div style={{ padding: "20px 20px 4px" }}>

                    {/* Stats */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                        <div style={{ position: "relative", borderRadius: 12, padding: 2 }}>
                            {isRunning && (
                                <div style={{
                                    position: "absolute", inset: 0, borderRadius: 12,
                                    background: "linear-gradient(90deg, #d1fae5 0%, #d1fae5 30%, #10b981 50%, #d1fae5 70%, #d1fae5 100%)",
                                    backgroundSize: "300% 100%",
                                    animation: "uc-border-spin 1.6s linear infinite",
                                }} />
                            )}
                            <div style={{
                                position: "relative", zIndex: 1,
                                borderRadius: 10, padding: "14px 16px",
                                background: isRunning ? "#ecfdf5" : "#f9fafb",
                                border: isRunning ? "none" : "1px solid #f0f0f0",
                                transition: "background 0.3s",
                            }}>
                                <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 500, color: isRunning ? "#059669" : "#9ca3af" }}>
                                    ASINs checked {isRunning ? <Dots /> : ""}
                                </p>
                                <p style={{
                                    margin: 0, fontSize: 30, fontWeight: 700, lineHeight: 1,
                                    color: isRunning ? "#059669" : "#111827",
                                    animation: isRunning ? "uc-pulse-num 0.4s ease-in-out infinite alternate" : "none"
                                }}>
                                    {stats.checked.toLocaleString()}
                                </p>
                                <p style={{ margin: "4px 0 0", fontSize: 10, color: brandListFinished ? "#059669" : "#9ca3af", lineHeight: 1, fontWeight: brandListFinished ? 700 : 400 }}>
                                    {brandListFinished && !isRunning
                                        ? "✅ Brand list scan complete"
                                        : started && !isRunning
                                            ? (brandListMode && brandListGroupInfo.total > 0
                                                ? `⏸ Paused — Group ${Math.max(brandListGroupInfo.current, 1)} of ${brandListGroupInfo.total}`
                                                : "⏸ Paused — press Start to resume")
                                            : brandListMode && brandListGroupInfo.total > 0
                                                ? `Group ${Math.max(brandListGroupInfo.current, 1)} of ${brandListGroupInfo.total}${queueSize > 0 ? ` · Queue: ${queueSize.toLocaleString()}` : ""}`
                                                : queueSize > 0 ? `Queue: ${queueSize.toLocaleString()}` : ""}
                                </p>
                            </div>
                        </div>
                        <div style={{
                            background: newBrandsThisRun.length > 0 ? "#ecfdf5" : "#f9fafb",
                            borderRadius: 12, padding: "14px 16px",
                            border: `1px solid ${newBrandsThisRun.length > 0 ? "#6ee7b7" : "#f0f0f0"}`,
                            transition: "all 0.3s"
                        }}>
                            <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 500, color: newBrandsThisRun.length > 0 ? "#059669" : "#9ca3af" }}>
                                New ungated brands found
                            </p>
                            <p style={{ margin: 0, fontSize: 30, fontWeight: 700, lineHeight: 1, color: newBrandsThisRun.length > 0 ? "#059669" : "#111827" }}>
                                {newBrandsThisRun.length}
                            </p>
                        </div>
                    </div>

                    {/* New ungated brands found */}
                    {newBrandsThisRun.length > 0 && (
                        <div style={{
                            background: "#ecfdf5", borderRadius: 12, padding: "10px 12px",
                            border: "1px solid #a7f3d0", marginBottom: 14,
                            maxHeight: 80, overflowY: "auto"
                        }}>
                            <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: "#065f46" }}>
                                ✅ Your new ungated brands:
                            </p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {newBrandsThisRun.map(b => (
                                    <span key={b} className="uc-badge">{b}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Found Interesting Products button ── */}
                    {foundProducts.length > 0 && (
                        <button
                            onClick={() => setShowFoundProducts(true)}
                            style={{
                                width: "100%", marginBottom: 14, padding: "11px 16px",
                                borderRadius: 10, border: "none",
                                background: "linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(13, 148, 136) 50%, rgb(37, 99, 235) 100%)",
                                cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 15 }}>🎯</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: "white" }}>
                                    See your ungated product leads!
                                </span>
                            </div>
                            <span style={{
                                background: "rgba(255,255,255,0.15)", color: "white", fontWeight: 700,
                                fontSize: 12, borderRadius: 999, padding: "2px 10px",
                                border: "1px solid rgba(255,255,255,0.25)"
                            }}>
                                {foundProducts.length}
                            </span>
                        </button>
                    )}

                    {/* Category filter note - no categories yet */}
                    {!started && availableCategories.length === 0 && (
                        <div style={{
                            marginBottom: 14, padding: "10px 14px", borderRadius: 10,
                            border: "1px dashed #e5e7eb", background: "#f9fafb",
                            fontSize: 12, color: "#9ca3af", textAlign: "center",
                        }}>
                            🎯 Focusing the crawler on specific categories will unlock once more categories are available.
                        </div>
                    )}

                    {/* Category filter button - only before starting, hidden in brand-list mode */}
                    {!started && availableCategories.length > 0 && !brandListMode && (
                        <div style={{ marginBottom: 14 }}>
                            <button
                                onClick={() => setShowCategoryPicker(true)}
                                style={{
                                    width: "100%", padding: "10px 16px", borderRadius: 10,
                                    border: `1px solid ${selectedCategories.length > 0 ? "#6ee7b7" : "#e5e7eb"}`,
                                    background: selectedCategories.length > 0 ? "#ecfdf5" : "#f9fafb",
                                    cursor: "pointer", display: "flex", alignItems: "center",
                                    justifyContent: "space-between", color: "#374151",
                                    fontWeight: 600, fontSize: 13,
                                }}
                            >
                                <span>🎯 Focus on categories</span>
                                <span style={{
                                    fontSize: 11, fontWeight: 700,
                                    color: selectedCategories.length > 0 ? "#059669" : "#9ca3af",
                                    background: selectedCategories.length > 0 ? "#d1fae5" : "#f3f4f6",
                                    padding: "2px 10px", borderRadius: 999,
                                }}>
                                    {selectedCategories.length === 0
                                        ? "All categories"
                                        : `${selectedCategories.length} selected`}
                                </span>
                            </button>
                        </div>
                    )}

                    {/* Brand list filter button - only before starting, hidden if category filter is active */}
                    {!started && selectedCategories.length === 0 && (
                        <div style={{ marginBottom: 14 }}>
                            <button
                                onClick={() => setShowBrandListPicker(true)}
                                style={{
                                    width: "100%", padding: "10px 16px", borderRadius: 10,
                                    border: `1px solid ${brandListMode ? "#6ee7b7" : "#e5e7eb"}`,
                                    background: brandListMode ? "#ecfdf5" : "#f9fafb",
                                    cursor: "pointer", display: "flex", alignItems: "center",
                                    justifyContent: "space-between", color: "#374151",
                                    fontWeight: 600, fontSize: 13,
                                }}
                            >
                                <span>📋 Check a brand list</span>
                                <span style={{
                                    fontSize: 11, fontWeight: 700,
                                    color: brandListMode ? "#059669" : "#9ca3af",
                                    background: brandListMode ? "#d1fae5" : "#f3f4f6",
                                    padding: "2px 10px", borderRadius: 999,
                                }}>
                                    {brandListMode
                                        ? `${parseBrandList(brandListText).length} brand(s)${chunkBrandList(brandListText).length > 1 ? ` (${chunkBrandList(brandListText).length} groups)` : ""}`
                                        : "Not set"}
                                </span>
                            </button>
                        </div>
                    )}

                    {/* YouTube - only after start */}
                    {started && (
                        <div style={{ marginBottom: 4 }}>
                            <button
                                onClick={() => setShowVideos(true)}
                                style={{
                                    width: "100%", padding: "10px 16px", borderRadius: 10,
                                    border: "1px solid #e5e7eb", background: "#f9fafb",
                                    cursor: "pointer", display: "flex", alignItems: "center",
                                    justifyContent: "center", gap: 8, color: "#374151",
                                    fontWeight: 600, fontSize: 13,
                                }}
                            >
                                <span>▶</span> Watch while you wait
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div style={{
                    display: "flex", gap: 8, padding: "16px 20px",
                    borderTop: "1px solid #f3f4f6", marginTop: 8
                }}>
                    {!isRunning ? (
                        <button
                            onClick={start}
                            disabled={loading}
                            style={{
                                flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                                background: loading ? "#6ee7b7" : "#059669", color: "white", fontWeight: 700,
                                cursor: loading ? "default" : "pointer",
                                fontSize: 13, cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 7
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                <polygon points="5,3 19,12 5,21" />
                            </svg>
                            {loading
                                ? "Setting up Ungate Crawler…"
                                : brandListMode && brandListFinished
                                    ? "Scan Brand List Again"
                                    : started ? "Resume Crawling" : "Start Crawling"}
                        </button>
                    ) : (
                        <button
                            onClick={stop}
                            style={{
                                flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                                background: "#ef4444", color: "white", fontWeight: 700,
                                fontSize: 13, cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 7
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                <rect x="4" y="4" width="16" height="16" />
                            </svg>
                            Stop
                        </button>
                    )}
                    <button
                        onClick={reset}
                        style={{
                            padding: "10px 16px", borderRadius: 10,
                            border: "1px solid #e5e7eb", background: "white",
                            color: "#6b7280", fontWeight: 600, fontSize: 12, cursor: "pointer"
                        }}
                    >
                        Reset
                    </button>
                </div>

            </div>
            {/* ── Found Products Popup ── */}
            {showFoundProducts && (
                <div
                    onClick={e => { if (e.target === e.currentTarget) setShowFoundProducts(false); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 70,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 540,
                        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
                        display: "flex", flexDirection: "column", overflow: "hidden",
                        maxHeight: "85vh",
                    }}>
                        {/* Header */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "16px 20px", borderBottom: "1px solid #f3f4f6", flexShrink: 0,
                        }}>
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>
                                    Interesting Ungated Products
                                </p>
                                <p style={{ margin: 0, fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                    You're ungated - use Universal Reverse Source to find a supplier!
                                </p>
                                <button
                                    onClick={exportFoundProducts}
                                    style={{
                                        marginTop: 6, padding: "5px 12px", borderRadius: 7,
                                        border: "1px solid #a7f3d0", background: "#ecfdf5",
                                        color: "#065f46", fontWeight: 600, fontSize: 11,
                                        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
                                    }}
                                >
                                    ⬇️ Export CSV ({foundProducts.length})
                                </button>
                            </div>
                            <button
                                onClick={() => setShowFoundProducts(false)}
                                style={{
                                    width: 28, height: 28, borderRadius: "50%", border: "none",
                                    background: "#f3f4f6", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#6b7280", flexShrink: 0,
                                }}
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {/* Product list */}
                        <div style={{ overflowY: "auto", maxHeight: "60vh", padding: "14px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                            {foundProducts.map(p => (
                                <div key={p.asin} style={{
                                    display: "flex", alignItems: "center", gap: 10,
                                    background: p.opened ? "#f9fafb" : "white",
                                    border: `1px solid ${p.opened ? "#e5e7eb" : "#d1fae5"}`,
                                    borderRadius: 10, padding: "10px 12px",
                                    opacity: p.opened ? 0.7 : 1,
                                    transition: "all 0.2s",
                                    position: "relative",
                                }}>
                                    <button
                                        title="Remove"
                                        onClick={() => setFoundProducts(prev => prev.filter(x => x.asin !== p.asin))}
                                        style={{
                                            position: "absolute", top: 4, left: 4,
                                            width: 16, height: 16, borderRadius: "50%",
                                            border: "none", background: "#fee2e2",
                                            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                            color: "#ef4444", fontSize: 9, fontWeight: 700, lineHeight: 1, padding: 0,
                                        }}
                                    >✕</button>
                                    {/* Image */}
                                    {p.image
                                        ? <img src={p.image} alt="" style={{ width: 46, height: 46, objectFit: "contain", borderRadius: 8, flexShrink: 0, background: "#f3f4f6" }} />
                                        : <div style={{ width: 46, height: 46, borderRadius: 8, background: "#f3f4f6", flexShrink: 0 }} />
                                    }

                                    {/* Info */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ margin: "0 0 3px", fontSize: 11, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {p.title || p.asin}
                                        </p>
                                        {p.brand && (
                                            <p style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 700, color: "#374151", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                {p.brand}
                                                {p.az_price > 0 && <span style={{ fontWeight: 400, fontSize: 11, color: "#6b7280" }}>${p.az_price.toFixed(2)}</span>}
                                            </p>
                                        )}
                                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                            {p.sales_rank > 0 && (
                                                <span style={{ fontSize: 9, background: "#f3f4f6", color: "#374151", borderRadius: 4, padding: "1px 6px" }}>
                                                    BSR #{p.sales_rank.toLocaleString()}
                                                </span>
                                            )}
                                            {p.sales_rank_drops_30 > 0 && (
                                                <span style={{ fontSize: 9, background: "#ecfdf5", color: "#065f46", borderRadius: 4, padding: "1px 6px" }}>
                                                    📉 {p.sales_rank_drops_30} drops/mo
                                                </span>
                                            )}
                                            {p.keepa_monthly_sold > 0 && (
                                                <span style={{ fontSize: 9, background: "#eff6ff", color: "#1d4ed8", borderRadius: 4, padding: "1px 6px" }}>
                                                    ✅ {p.keepa_monthly_sold.toLocaleString()} sold/mo
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                                        {/* Google / Universal Reverse Scan */}
                                        <button
                                            title="Universal Reverse Scan on Google"
                                            onClick={() => {
                                                const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                                if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                                    window.chrome.runtime.sendMessage(EXT_ID, {
                                                        type: 'MSP_SEARCH_INTENT',
                                                        marketplace: 'amazon',
                                                        identifier: p.asin,
                                                        title: p.title,
                                                    });
                                                }
                                                if (p.title) window.open(`https://www.google.com/search?q=${encodeURIComponent(p.title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                                setFoundProducts(prev => prev.map(x => x.asin === p.asin ? { ...x, opened: true } : x));
                                            }}
                                            style={{
                                                width: 30, height: 30, borderRadius: 8, padding: 0,
                                                border: "1px solid #e5e7eb", background: "white",
                                                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                            }}
                                        >
                                            <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{ width: 16, height: 16, objectFit: "contain" }} />
                                        </button>
                                        {/* Amazon product page */}
                                        <button
                                            title="Open product on Amazon"
                                            onClick={() => window.open(`https://www.amazon${authService.getAmazonMarketplace()}/dp/${p.asin}`, '_blank')}
                                            style={{
                                                width: 30, height: 30, borderRadius: 8, padding: 0,
                                                border: "1px solid #e5e7eb", background: "white",
                                                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 14,
                                            }}
                                        >
                                            🔗
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Category Picker Popup ── */}
            {showCategoryPicker && (
                <div
                    onClick={e => { if (e.target === e.currentTarget) setShowCategoryPicker(false); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 70,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 480,
                        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
                        display: "flex", flexDirection: "column", overflow: "hidden",
                        maxHeight: "75vh",
                    }}>
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>Focus on Categories</p>
                                <p style={{ margin: 0, fontSize: 11, color: "#6b7280", marginTop: 2 }}>Crawler will only focus on brands in selected categories</p>
                            </div>
                            <button onClick={() => setShowCategoryPicker(false)} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: "#f3f4f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                                <X size={14} />
                            </button>
                        </div>

                        {/* Select All / Clear */}
                        <div style={{ padding: "10px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", gap: 8 }}>
                            <button
                                onClick={() => setSelectedCategories([...availableCategories])}
                                style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #d1fae5", background: "#ecfdf5", color: "#065f46", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => setSelectedCategories([])}
                                style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #e5e7eb", background: "white", color: "#6b7280", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                            >
                                Clear (All Categories)
                            </button>
                            <span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af", alignSelf: "center" }}>
                                {selectedCategories.length === 0 ? "All" : selectedCategories.length} / {availableCategories.length}
                            </span>
                        </div>

                        {/* Category list */}
                        <div style={{ overflowY: "auto", padding: "12px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
                            {availableCategories.map(cat => {
                                const isChecked = selectedCategories.includes(cat);
                                return (
                                    <label key={cat} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: isChecked ? "#ecfdf5" : "#f9fafb", border: `1px solid ${isChecked ? "#6ee7b7" : "#f0f0f0"}`, cursor: "pointer", transition: "all 0.15s" }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => setSelectedCategories(prev =>
                                                prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
                                            )}
                                            style={{ accentColor: "#059669", width: 15, height: 15, flexShrink: 0 }}
                                        />
                                        <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{cat}</span>
                                    </label>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: "14px 20px", borderTop: "1px solid #f3f4f6" }}>
                            <button
                                onClick={() => setShowCategoryPicker(false)}
                                style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#059669", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                            >
                                Apply — {selectedCategories.length === 0 ? "Crawl All Categories" : `Focus on ${selectedCategories.length} Categor${selectedCategories.length === 1 ? "y" : "ies"}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Brand List Picker Popup ── */}
            {showBrandListPicker && (
                <div
                    onClick={e => { if (e.target === e.currentTarget) setShowBrandListPicker(false); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 70,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 480,
                        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
                        display: "flex", flexDirection: "column", overflow: "hidden",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>Filter by Brand Name</p>
                                <p style={{ margin: 0, fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                    Paste brand names, one per line or comma-separated. The crawler will only check these.
                                </p>
                            </div>
                            <button onClick={() => setShowBrandListPicker(false)} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: "#f3f4f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                                <X size={14} />
                            </button>
                        </div>
                        <div style={{ padding: "16px 20px" }}>
                            <textarea
                                value={brandListText}
                                onChange={e => setBrandListText(e.target.value)}
                                rows={8}
                                placeholder={"Nike\nAdidas, Puma"}
                                style={{
                                    width: "100%", padding: "10px 12px", borderRadius: 10,
                                    border: "1px solid #e5e7eb", fontSize: 13, resize: "vertical",
                                }}
                            />
                            <p style={{ margin: "8px 0 0", fontSize: 11, color: "#9ca3af" }}>
                                {parseBrandList(brandListText).length} unique brand(s) parsed
                                {parseBrandList(brandListText).length > MAX_BRANDS_PER_RUN
                                    ? ` — will run in ${chunkBrandList(brandListText).length} groups of up to ${MAX_BRANDS_PER_RUN}, one after another.`
                                    : `.`}
                            </p>
                        </div>
                        <div style={{ padding: "14px 20px", borderTop: "1px solid #f3f4f6", display: "flex", gap: 8 }}>
                            <button
                                onClick={() => { setBrandListText(""); setBrandListMode(false); setShowBrandListPicker(false); }}
                                style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid #e5e7eb", background: "white", color: "#6b7280", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                            >
                                Clear
                            </button>
                            <button
                                onClick={() => { setBrandListMode(parseBrandList(brandListText).length > 0); setShowBrandListPicker(false); }}
                                style={{ flex: 2, padding: "10px", borderRadius: 10, border: "none", background: "#059669", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                            >
                                Apply — {parseBrandList(brandListText).length} Brand(s)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Videos Popup ── */}
            {showVideos && (
                <div
                    onClick={e => { if (e.target === e.currentTarget) setShowVideos(false); }}
                    style={{
                        position: "fixed", inset: 0, zIndex: 70,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div style={{
                        background: "white", borderRadius: 20, width: "100%", maxWidth: 820,
                        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
                        display: "flex", flexDirection: "column", overflow: "hidden",
                    }}>
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "16px 20px", borderBottom: "1px solid #f3f4f6",
                        }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>Watch while you wait</p>
                            <button
                                onClick={() => setShowVideos(false)}
                                style={{
                                    width: 28, height: 28, borderRadius: "50%", border: "none",
                                    background: "#f3f4f6", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280",
                                }}
                            ><X size={14} /></button>
                        </div>
                        <div style={{ padding: "16px 20px 20px" }}>
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <button
                                    onClick={() => setYtIdx(i => (i - 1 + YT_VIDEOS.length) % YT_VIDEOS.length)}
                                    style={{
                                        position: "absolute", left: -17, zIndex: 2,
                                        width: 34, height: 34, borderRadius: "50%", border: "1.5px solid black",
                                        background: "white", boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#374151",
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                                </button>
                                <div style={{
                                    flex: 1, borderRadius: 12, overflow: "hidden",
                                    border: "1px solid #e5e7eb", background: "#000", aspectRatio: "16/9",
                                }}>
                                    <iframe
                                        key={ytIdx}
                                        width="100%" height="100%"
                                        src={`https://www.youtube.com/embed/${YT_VIDEOS[ytIdx]}?controls=1`}
                                        allow="autoplay; encrypted-media"
                                        allowFullScreen
                                        style={{ display: "block", border: "none" }}
                                    />
                                </div>
                                <button
                                    onClick={() => setYtIdx(i => (i + 1) % YT_VIDEOS.length)}
                                    style={{
                                        position: "absolute", right: -17, zIndex: 2,
                                        width: 34, height: 34, borderRadius: "50%", border: "1.5px solid black",
                                        background: "white", boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#374151",
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                                </button>
                            </div>
                            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8 }}>
                                {YT_VIDEOS.map((_, i) => (
                                    <button key={i} onClick={() => setYtIdx(i)} style={{
                                        width: 7, height: 7, borderRadius: "50%", border: "none",
                                        cursor: "pointer", padding: 0,
                                        background: i === ytIdx ? "#059669" : "#d1d5db",
                                    }} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}